import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { CrewEntry } from "../components/callsheet/types/callsheet";
import { RootState } from "../../../store/Index";
import { BaseURL } from "../../../service/Api";
import apiService from "../../../service/Api";
import ChecklistDetailDrawer from "../components/callsheet/ChecklistDetailDrawer";

interface ChecklistSummaryItem {
  checklist_id: number;
  checklist_name: string;
  role_id: number | null;
  total: number;
  completed: number;
}

interface UserChecklistSummary {
  user_id: number;
  checklists: ChecklistSummaryItem[];
}

interface CrewAssignmentTableProps {
  crewMembersData: CrewEntry[];
  streamId?: number;
}

const CrewAssignmentTable: React.FC<CrewAssignmentTableProps> = ({
  crewMembersData,
  streamId,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [checklistSummaries, setChecklistSummaries] = useState<
    Record<number, ChecklistSummaryItem[]>
  >({});
  const [drawerChecklist, setDrawerChecklist] = useState<{
    userId: number;
    checklistId: number;
    checklistName: string;
    percent?: number;
    completed?: number;
    total?: number;
  } | null>(null);

  const fetchChecklistSummaries = useCallback(async () => {
    if (!streamId) return;
    try {
      const res = await apiService.get<UserChecklistSummary[]>(
        `/api/checklists/checklist-progress/stream-summary/?stream=${streamId}`,
      );
      const map: Record<number, ChecklistSummaryItem[]> = {};
      res.data.forEach((entry) => {
        map[entry.user_id] = entry.checklists;
      });
      setChecklistSummaries(map);
    } catch (error) {
      console.error("Failed to load checklist summaries:", error);
      setChecklistSummaries({});
    }
  }, [streamId]);

  useEffect(() => {
    fetchChecklistSummaries();
  }, [fetchChecklistSummaries]);

  const roleOrder = [
    "Executive Producer",
    "Producer",
    "Assistant Producer",
    "Liveswitcher",
    "Camera",
    "Networking",
    "Sound",
    "Tribute Table",
  ];

  const sortedCrewMembers = [...crewMembersData].sort((a, b) => {
    const roleA = a.roles?.[0]?.name || a.assignment?.[0]?.role || "N/A";
    const roleB = b.roles?.[0]?.name || b.assignment?.[0]?.role || "N/A";

    const indexA = roleOrder.findIndex(
      (role) => role.toLowerCase() === roleA.toLowerCase(),
    );
    const indexB = roleOrder.findIndex(
      (role) => role.toLowerCase() === roleB.toLowerCase(),
    );

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  const handleSendCallsheet = () => {
    const pdfLink = `${BaseURL}/stream-management/view/${streamId}/callsheet`;

    const message = encodeURIComponent(
      `Hello, this is your callsheet for the event.\n\nDownload PDF here:\n${pdfLink}`,
    );

    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  return (
    <Card sx={{ boxShadow: 0, height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {" "}
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Crew Assignment
          </Typography>
          <Button
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              color: reduxTheme.primary_colors[900],
            }}
            onClick={() => handleSendCallsheet()}
          >
            Send Callsheet
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{ backgroundColor: reduxTheme.primary_colors[800] }}
              >
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Position
                </TableCell>
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    color: reduxTheme.primary_colors[100],
                    fontWeight: "bold",
                  }}
                >
                  Checklists
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCrewMembers.map((entry, index) => {
                const crewMember = entry.crew_member;
                const user = crewMember.user;

                const role =
                  entry?.roles?.[0]?.name ||
                  entry?.assignment?.[0]?.role ||
                  "N/A";
                const roleId = entry?.roles?.[0]?.id ?? null; // real ID, not a guess from text

                const fullName = `${user.first_name} ${user.last_name}`.trim();
                const email = user.email || "";
                const allUserChecklists = user?.id
                  ? (checklistSummaries[user.id] ?? [])
                  : [];

                const checklists = roleId
                  ? allUserChecklists.filter((cl) => cl.role_id === roleId)
                  : [];
                // Get status color
                const getStatusColor = (status?: string) => {
                  switch (status) {
                    case "accepted":
                      return "#4BA663"; // green
                    case "declined":
                      return "#EB4335"; // red
                    case "pending":
                      return "#F79E1B"; // light orange
                    default:
                      return "#BDBDBD"; // gray
                  }
                };

                return (
                  <motion.tr
                    key={entry.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? "transparent"
                          : reduxTheme.primary_colors[800],
                      cursor: "pointer",
                    }}
                  >
                    <TableCell sx={{ fontSize: "0.85rem" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {fullName}
                        {entry.status && (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              backgroundColor: getStatusColor(entry.status),
                              display: "inline-block",
                            }}
                            title={`Status: ${entry.status}`}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.85rem" }}>{role}</TableCell>
                    <TableCell sx={{ fontSize: "0.85rem" }}>{email}</TableCell>
                    <TableCell sx={{ fontSize: "0.85rem" }}>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {checklists.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            None assigned
                          </Typography>
                        ) : (
                          checklists.map((cl) => {
                            const done = cl.completed === cl.total;
                            return (
                              <Chip
                                key={cl.checklist_id}
                                label={`${cl.checklist_name}: ${cl.completed}/${cl.total}`}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!user?.id) return;
                                  const percent =
                                    cl.total > 0
                                      ? Math.round(
                                          (cl.completed / cl.total) * 100,
                                        )
                                      : 0;
                                  setDrawerChecklist({
                                    userId: user.id,
                                    checklistId: cl.checklist_id,
                                    checklistName: cl.checklist_name,
                                    percent,
                                    completed: cl.completed,
                                    total: cl.total,
                                  });
                                }}
                                sx={{
                                  backgroundColor: done
                                    ? "#4BA66322"
                                    : "#F79E1B22",
                                  color: done ? "#4BA663" : "#F79E1B",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                }}
                              />
                            );
                          })
                        )}
                      </Box>
                    </TableCell>
                  </motion.tr>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {drawerChecklist && streamId && (
        <ChecklistDetailDrawer
          open={!!drawerChecklist}
          onClose={() => setDrawerChecklist(null)}
          streamId={streamId}
          userId={drawerChecklist.userId}
          checklistId={drawerChecklist.checklistId}
          checklistName={drawerChecklist.checklistName}
          percent={drawerChecklist.percent ?? 0}
          completed={drawerChecklist.completed ?? 0}
          total={drawerChecklist.total ?? 0}
        />
      )}
    </Card>
  );
};

export default CrewAssignmentTable;
